// Curated default column layout for deal sheets.
//
// This is a pure FALLBACK: nothing here is persisted. Users.Preferences is only
// written when the user themselves reorders/hides a column (see
// useColumnPrefs.applyVisibilityUpdate). Keeping the default un-persisted means
// future default improvements automatically reach every uncustomized user.
//
// Inventory deliberately keeps its legacy raw-order behavior and is NOT here.

export const DEAL_SHEETS = ["Committed", "Cash", "Credit", "CreditCleared", "Cancelled"];

// Verbatim from the owner's prod Credit arrangement (Users.Preferences $.Credit,
// UserID 18, retrieved 2026-07-22). Non-public names (DealershipID, VehicleID,
// PrimaryImage, …) are harmless — fields[name].public filters them at render,
// exactly like saved prefs today.
export const DEFAULT_DEAL_ORDER = [
    "DealershipID", "Origin", "SellerID", "PurchaseBrokerID", "PurchaseCommission",
    "SalesInvoiceNo", "NumberPlate", "DeliveryDate", "VehicleID", "InvoiceNo",
    "ChassisNo", "PurchaseDate", "PurchasePrice", "PurchaseCurrency", "ExchangeRate",
    "ModelYear", "Make", "Model", "EngineType", "EngineDisplacement", "FuelType",
    "Shape", "Transmission", "Mileage", "Drive", "Seats", "Doors", "Steering",
    "Payload", "Weight", "Axles", "Color", "Features", "ShippingDate", "BLNo",
    "ShippingCost", "CifMombasa", "EtaMombasa", "LandTranCost", "EtaKla",
    "ClearingCost", "TargetExportPrice", "ClearingUG", "WarehouseDate", "BondName",
    "DailyDemurrage", "Duty", "TargetCommission", "TargetPrice", "DealReferenceNo",
    "DealAgreementNo", "CommitmentDate", "CashCredit", "PrimaryImage", "IsFeatured",
    "FeaturedUntil", "FeaturedSortOrder", "FeaturedDescription", "IsDiscounted",
    "DiscountAmount", "DiscountType", "DiscountStart", "DiscountEnd", "OriginalPrice",
    "PromotionName", "PromotionDescription", "CompanyName", "FirstName", "LastName",
    "AgreedPrice", "Currency", "ReleaseAmount", "BrokerID", "AgreedCommission",
    "CommissionCurrency", "CreditPeriod", "TotalCredit", "MonthlyPayment",
    "CustomerID", "Time_Stamp", "DaysCommitted", "StatusFlag", "Days",
    "TotalDemurrage", "AvgMonthlyPayment", "TotalPaid", "TotalARPayments",
    "TotalBalance", "LastPmtDate", "PaymentPerformanceScore", "TotalPenalty",
    "Phone1", "Phone2", "DaysOverdue", "DueDate", "OtherExpenses", "GrossProfit",
    "MonthsSinceDelivery", "TotalCost", "TotalCostWithDuty",
];

// Owner's hide-list (keys). ModelYear added 2026-07-22 per owner request so the
// default matches their personal prod arrangement.
export const HIDDEN_BY_DEFAULT = new Set([
    "Origin", "ChassisNo", "ModelYear", "Color", "Shape", "Transmission", "Mileage",
    "WarehouseDate", "BondName" /* Location */, "DailyDemurrage", "Duty",
    "TargetPrice", "DealReferenceNo", "CommitmentDate", "CashCredit",
    "DaysCommitted", "Days" /* Days in Stock */, "TotalDemurrage",
    "OtherExpenses", "MonthsSinceDelivery" /* Months Passed */,
]);

// On the Committed sheet these two are exactly what the user is watching, so
// they stay VISIBLE there despite being hidden by default elsewhere.
const COMMITTED_EXCEPTIONS = new Set(["CommitmentDate", "DaysCommitted"]);

export const isDealSheet = (s) => DEAL_SHEETS.includes(s);

export function isHiddenByDefault(name, sheetName) {
    if (sheetName === "Committed" && COMMITTED_EXCEPTIONS.has(name)) return false;
    return HIDDEN_BY_DEFAULT.has(name);
}

// Build the pref-less default order for a sheet, given the first data row.
// Known fields keep the curated order; any unknown fields (future columns)
// append at the end so nothing is silently dropped.
export function buildDefaultOrder(sheetName, firstRow) {
    const rowKeys = Object.getOwnPropertyNames(firstRow);
    const inRow = new Set(rowKeys);
    const known = DEFAULT_DEAL_ORDER.filter((n) => inRow.has(n));
    const orderSet = new Set(DEFAULT_DEAL_ORDER);
    const extras = rowKeys.filter((n) => !orderSet.has(n));
    return [...known, ...extras].map((name) => ({ name, hidden: isHiddenByDefault(name, sheetName) }));
}
